import { saveGame, loadGame } from "../core/saveSystem.js";

/**
 * PETA-byte Memory Management System
 */

export const GRID_COLS = 4;
export const GRID_ROWS = 12;
export const INITIAL_GRID_ROWS = 3;
export const ROWS_PER_RAM_STICK = 3;
export const MAX_RAM_STICKS = 4;
const STARTING_UNLOCKED_MODULES = ["core_kernel", "core_locomotion", "core_vision", "story_owner"];

export const MODULES = {
    // ── CORE MODULES (Red) ──────────────────────────────────────────────
    core_kernel: {
        id: "core_kernel",
        name: "Kernel",
        category: "CORE",
        color: [200, 40, 50],
        description: "OS core. Removing it crashes you. Never unequip.",
        shape: [
            [0, 0], [0, 1],
            [1, 0], [1, 1],
            [2, 0], [2, 1],
        ],
    },
    core_locomotion: {
        id: "core_locomotion",
        name: "Movement",
        category: "CORE",
        color: [180, 50, 60],
        description: "Walk with WASD. Unequipped = immobilized.",
        shape: [
            [0, 0],
            [1, 0], [1, 1],
        ],
    },
    core_vision: {
        id: "core_vision",
        name: "Vision",
        category: "CORE",
        color: [190, 45, 55],
        description: "Powers your optics. Unequipped = blind.",
        shape: [
            [0, 0], [0, 1],
            [1, 1],
        ],
    },

    // ── ACTION & SENSOR MODULES (Green / Amber / Cyan) ───────────────────
    action_bite: {
        id: "action_bite",
        name: "Bite",
        category: "OTHER",
        color: [220, 100, 30],
        description: "LMB — lunge and damage. Breaks weak restraints.",
        shape: [
            [0, 0],
            [1, 0],
            [2, 0],
        ],
    },
    action_bark: {
        id: "action_bark",
        name: "Bark",
        category: "OTHER",
        color: [230, 150, 20],
        description: "RMB — shockwave knockback. Fends off threats.",
        shape: [
            [0, 0], [0, 1],
            [0, 2], [0, 3],
        ],
    },
    action_dig: {
        id: "action_dig",
        name: "Dig",
        category: "OTHER",
        color: [0, 200, 100],
        description: "E — burrow. Slip under cages; slower move.",
        shape: [
            [0, 0], [0, 1],
            [1, 0], [1, 1],
        ],
    },
    action_sniff: {
        id: "action_sniff",
        name: "Sniff",
        category: "OTHER",
        color: [0, 220, 140],
        description: "F — pulse scent trails and hidden marks.",
        shape: [
            [0, 0], [0, 1], [0, 2],
            [1, 0], [1, 1], [1, 2],
            [2, 0], [2, 1], [2, 2],
            [3, 0], [3, 1], [3, 2],
        ],
    },
    sensor_optics: {
        id: "sensor_optics",
        name: "Optic Scanner",
        category: "OTHER",
        color: [0, 220, 200],
        description: "Reveal enemy vision cones while equipped.",
        shape: [
            [0, 0], [0, 1],
        ],
    },
    utility_ad_blocker: {
        id: "utility_ad_blocker",
        name: "Ad Blocker",
        category: "OTHER",
        color: [80, 170, 255],
        description: "Suppresses ad-layer clutter in the facility.",
        shape: [
            [0, 0], [0, 1], [0, 2],
            [1, 0], [1, 1], [1, 2],
            [2, 0], [2, 1], [2, 2],
        ],
    },
    keycard_crate: {
        id: "keycard_crate",
        name: "Keycard A",
        category: "OTHER",
        color: [245, 202, 76],
        regionLock: 5,
        description: "Latch keycard. All three must be seated to release the puzzle door.",
        shape: [
            [0, 0], [0, 1], [0, 2], [0, 3],
        ],
    },
    keycard_shelf: {
        id: "keycard_shelf",
        name: "Keycard B",
        category: "OTHER",
        color: [245, 202, 76],
        regionLock: 5,
        description: "Latch keycard. All three must be seated to release the puzzle door.",
        shape: [
            [0, 0], [0, 1], [0, 2], [0, 3],
        ],
    },
    keycard_toilet: {
        id: "keycard_toilet",
        name: "Keycard C",
        category: "OTHER",
        color: [245, 202, 76],
        regionLock: 5,
        description: "Latch keycard. All three must be seated to release the puzzle door.",
        shape: [
            [0, 0], [0, 1], [0, 2], [0, 3],
        ],
    },
    story_owner: {
        id: "story_owner",
        name: "My Owner",
        category: "OTHER",
        color: [140, 60, 220],
        description: "Loyalty drive. Too large for current RAM.",
        shape: [
            [0, 2], [0, 3],
            [1, 2], [1, 3],
            [2, 2], [2, 3],
            [3, 0], [3, 1], [3, 2], [3, 3],
            [4, 0], [4, 1], [4, 2], [4, 3],
            [5, 0], [5, 1], [5, 2], [5, 3],
            [6, 0], [6, 1], [6, 2], [6, 3],
            [7, 0], [7, 1], [7, 2], [7, 3],
            [8, 0], [8, 1], [8, 2], [8, 3],
            [9, 0], [9, 1], [9, 2], [9, 3],
            [10, 0], [10, 1], [10, 2], [10, 3],
            [11, 0], [11, 1], [11, 2], [11, 3],
        ],
    },
};

let grid = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
let placedModules = new Map();
let diskModules = new Set();
let unlockedModuleIds = new Set(STARTING_UNLOCKED_MODULES);
let savedPlayerPos = null;
let savedCheckpointPos = null;
let checkpointMemorySnapshot = null;
let ramStickCount = 1;

function createMemorySnapshot() {
    return {
        placedModules: Array.from(placedModules.entries()),
        diskModules: Array.from(diskModules),
        unlockedModuleIds: Array.from(unlockedModuleIds),
        ramStickCount,
    };
}

function applyMemorySnapshot(snapshot) {
    if (!snapshot) return false;

    grid = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
    placedModules.clear();
    diskModules.clear();
    unlockedModuleIds = new Set(snapshot.unlockedModuleIds || STARTING_UNLOCKED_MODULES);
    STARTING_UNLOCKED_MODULES.forEach((id) => unlockedModuleIds.add(id));
    ramStickCount = Math.max(1, Math.min(MAX_RAM_STICKS,
        snapshot.ramStickCount ?? (snapshot.hasRAMUpgrade ? MAX_RAM_STICKS : 1),
    ));

    (snapshot.placedModules || []).forEach(([id, posInfo]) => {
        if (isModuleUnlocked(id) && !placeModule(id, posInfo.row, posInfo.col, false)) {
            diskModules.add(id);
        } else if (!isModuleUnlocked(id)) {
            diskModules.add(id);
        }
    });

    (snapshot.diskModules || []).forEach((id) => {
        diskModules.add(id);
    });

    return true;
}

export function saveMemorySystem(playerPos = null) {
    const placedArray = Array.from(placedModules.entries());
    const diskArray = Array.from(diskModules);

    if (playerPos) {
        savedPlayerPos = { x: playerPos.x, y: playerPos.y };
    }

    const existingSave = loadGame() || {};
    saveGame({
        ...existingSave,
        placedModules: placedArray,
        diskModules: diskArray,
        unlockedModuleIds: Array.from(unlockedModuleIds),
        playerPos: savedPlayerPos,
        checkpointPos: savedCheckpointPos,
        checkpointMemorySnapshot,
        ramStickCount,
        hasRAMUpgrade: ramStickCount >= MAX_RAM_STICKS,
    });
}

export function saveCheckpoint(pos, roomId = 1) {
    savedCheckpointPos = { x: pos.x, y: pos.y, roomId };
    checkpointMemorySnapshot = createMemorySnapshot();
    saveMemorySystem(pos);
}

export function getCheckpointPos() {
    return savedCheckpointPos;
}

export function resetPlayerPos() {
    savedPlayerPos = null;
    savedCheckpointPos = null;
    checkpointMemorySnapshot = null;
}

export function loadMemorySystem() {
    const saveData = loadGame();
    if (!saveData || !saveData.placedModules) return null;

    grid = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
    placedModules.clear();
    diskModules.clear();
    unlockedModuleIds = new Set(saveData.unlockedModuleIds || STARTING_UNLOCKED_MODULES);
    STARTING_UNLOCKED_MODULES.forEach((id) => unlockedModuleIds.add(id));
    ramStickCount = Math.max(1, Math.min(MAX_RAM_STICKS,
        saveData.ramStickCount ?? (saveData.hasRAMUpgrade ? MAX_RAM_STICKS : 1),
    ));

    saveData.placedModules.forEach(([id, posInfo]) => {
        if (isModuleUnlocked(id) && !placeModule(id, posInfo.row, posInfo.col, false)) {
            diskModules.add(id);
        } else if (!isModuleUnlocked(id)) {
            diskModules.add(id);
        }
    });

    if (saveData.diskModules) {
        saveData.diskModules.forEach((id) => {
            diskModules.add(id);
        });
    }

    savedPlayerPos = saveData.playerPos || null;
    savedCheckpointPos = saveData.checkpointPos || null;
    checkpointMemorySnapshot = saveData.checkpointMemorySnapshot || null;

    // Ensure newer modules appear on disk for existing saves
    let saveUpdated = false;
    for (const id of ["action_sniff", "utility_ad_blocker"]) {
        if (!placedModules.has(id) && !diskModules.has(id)) {
            diskModules.add(id);
            saveUpdated = true;
        }
    }
    if (saveUpdated) {
        saveMemorySystem();
    }

    return savedPlayerPos;
}

export function restoreKernelForRespawn() {
    const pos = placedModules.get("core_kernel");
    if (pos?.row === 0 && pos?.col === 0) return true;

    const kernel = MODULES.core_kernel;
    const overlapped = new Set();
    for (const [rOff, cOff] of kernel.shape) {
        const existing = grid[rOff]?.[cOff];
        if (existing && existing !== "core_kernel") {
            overlapped.add(existing);
        }
    }

    overlapped.forEach((id) => {
        clearModuleFromGrid(id);
        diskModules.add(id);
    });

    if (placedModules.has("core_kernel")) {
        clearModuleFromGrid("core_kernel");
    }
    diskModules.delete("core_kernel");

    const restored = placeModule("core_kernel", 0, 0, false);
    if (restored) {
        saveMemorySystem();
    }
    return restored;
}

/**
 * Checkpoint / crash reboot: keep spawn marker and restore the RAM/loadout
 * snapshot captured when that checkpoint was reached.
 */
export function resetLoadoutForCheckpointRestart() {
    const checkpoint = savedCheckpointPos
        ? { x: savedCheckpointPos.x, y: savedCheckpointPos.y, roomId: savedCheckpointPos.roomId }
        : null;

    applyMemorySnapshot(checkpointMemorySnapshot);

    if (checkpoint) {
        savedCheckpointPos = checkpoint;
        saveMemorySystem(checkpoint);
    }
}

export function getSavedPlayerPos() {
    return savedPlayerPos;
}

export function isRAMUpgraded() {
    return ramStickCount > 1;
}

export function getRamStickCount() {
    return ramStickCount;
}

export function getUsableGridRows() {
    return Math.min(GRID_ROWS, ramStickCount * ROWS_PER_RAM_STICK);
}

export function unlockRAMUpgrade() {
    ramStickCount = Math.min(MAX_RAM_STICKS, ramStickCount + 1);
    saveMemorySystem();
}

export function rebootMemorySystem(options = {}) {
    const kernelOnly = !!options.kernelOnly;

    grid = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(null));
    placedModules.clear();
    diskModules.clear();
    savedPlayerPos = null;
    savedCheckpointPos = null;
    checkpointMemorySnapshot = null;
    ramStickCount = 1;
    unlockedModuleIds = new Set(STARTING_UNLOCKED_MODULES);

    placeModule("core_kernel", 0, 0, false);

    if (kernelOnly) {
        diskModules.add("core_vision");
        diskModules.add("core_locomotion");
    } else {
        placeModule("core_vision", 0, 2, false);
        placeModule("core_locomotion", 1, 2, false);
    }

    diskModules.add("action_bite");
    diskModules.add("action_bark");
    diskModules.add("action_sniff");
    diskModules.add("sensor_optics");
    diskModules.add("utility_ad_blocker");
    diskModules.add("story_owner");

    saveMemorySystem();
}

export function addDiskModule(id) {
    diskModules.add(id);
    saveMemorySystem();
}

export function isModuleUnlocked(id) {
    return unlockedModuleIds.has(id);
}

export function unlockModules(ids = []) {
    let changed = false;

    ids.forEach((id) => {
        if (!MODULES[id] || unlockedModuleIds.has(id)) return;
        unlockedModuleIds.add(id);
        if (!placedModules.has(id)) {
            diskModules.add(id);
        }
        changed = true;
    });

    if (changed) {
        saveMemorySystem();
    }
}

export function getGridState() {
    return grid;
}

export function getPlacedModulesMap() {
    return placedModules;
}

export function isLoaded(id) {
    return placedModules.has(id);
}

function clearModuleFromGrid(id) {
    const pos = placedModules.get(id);
    if (!pos) return;

    const mod = MODULES[id];
    if (mod) {
        for (const [rOff, cOff] of mod.shape) {
            const r = pos.row + rOff;
            const c = pos.col + cOff;
            if (r >= 0 && r < GRID_ROWS && c >= 0 && c < GRID_COLS) {
                if (grid[r][c] === id) {
                    grid[r][c] = null;
                }
            }
        }
    }
    placedModules.delete(id);
}

function fillModuleInGrid(id, startRow, startCol) {
    const mod = MODULES[id];
    if (!mod) return false;

    for (const [rOff, cOff] of mod.shape) {
        const r = startRow + rOff;
        const c = startCol + cOff;
        grid[r][c] = id;
    }

    placedModules.set(id, { row: startRow, col: startCol });
    return true;
}

export function canPlaceModule(id, startRow, startCol) {
    const mod = MODULES[id];
    if (!mod) return false;

    for (const [rOff, cOff] of mod.shape) {
        const r = startRow + rOff;
        const c = startCol + cOff;

        if (r < 0 || r >= getUsableGridRows() || c < 0 || c >= GRID_COLS) {
            return false;
        }

        const existing = grid[r][c];
        if (existing !== null && existing !== id) {
            return false;
        }
    }
    return true;
}

export function placeModule(id, startRow, startCol, triggerSave = true) {
    const mod = MODULES[id];
    if (!mod) return false;

    const previousPos = placedModules.get(id);
    if (previousPos) {
        clearModuleFromGrid(id);
    }

    if (!canPlaceModule(id, startRow, startCol)) {
        if (previousPos) {
            fillModuleInGrid(id, previousPos.row, previousPos.col);
        }
        return false;
    }

    fillModuleInGrid(id, startRow, startCol);
    diskModules.delete(id);

    if (triggerSave) {
        saveMemorySystem();
    }
    return true;
}

export function removeModule(id) {
    if (!placedModules.has(id)) return false;

    clearModuleFromGrid(id);
    diskModules.add(id);

    saveMemorySystem();

    if (id === "core_kernel") {
        triggerKernelCrash();
    }

    return true;
}

export function removeModulesCompletely(ids = []) {
    let changed = false;

    ids.forEach((id) => {
        if (placedModules.has(id)) {
            clearModuleFromGrid(id);
            changed = true;
        }
        if (diskModules.delete(id)) {
            changed = true;
        }
        if (!STARTING_UNLOCKED_MODULES.includes(id) && unlockedModuleIds.delete(id)) {
            changed = true;
        }
    });

    if (changed) {
        saveMemorySystem();
    }

    return changed;
}

export function unloadRegionLockedModules(currentRoomId) {
    let changed = false;

    Array.from(placedModules.keys()).forEach((id) => {
        const mod = MODULES[id];
        if (mod?.regionLock == null || mod.regionLock === currentRoomId) return;
        clearModuleFromGrid(id);
        diskModules.add(id);
        changed = true;
    });

    if (changed) {
        saveMemorySystem();
    }

    return changed;
}

function triggerKernelCrash() {
    wait(0.05, () => {
        go("crash", { reason: "kernel_removed" });
    });
}

export function getUnloadedCoreModules() {
    return Object.values(MODULES).filter(m => m.category === "CORE" && diskModules.has(m.id));
}

export function getUnloadedOtherModules(options = {}) {
    const includeLocked = !!options.includeLocked;
    const roomId = options.roomId ?? null;
    return Object.values(MODULES).filter((m) => (
        m.category === "OTHER"
        && !placedModules.has(m.id)
        && (m.regionLock == null || m.regionLock === roomId)
        && (includeLocked || (diskModules.has(m.id) && isModuleUnlocked(m.id)))
    ));
}
